import React, { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';

import { getBasketSize, getSales, PerformanceByVenueTotal } from 'apis/merchant-gateway/insights';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { InfiniteTable } from 'modules/experience-core';

export enum PerformanceByVenueTableColumnIDs {
  NAME = 'NAME',
  SALES = 'SALES_WITH_VAT',
  ORDERS = 'ORDERS',
  AVG_BASKET_SIZE_WITH_VAT = 'AVG_BASKET_SIZE_WITH_VAT',
  AVG_BASKET_SIZE_NO_VAT = 'AVG_BASKET_SIZE_NO_VAT',
  AVG_BASKET_SIZE_ITEMS = 'AVG_BASKET_SIZE_ITEMS',
}

export type VenueTableSortableColumnIDs =
  | PerformanceByVenueTableColumnIDs.SALES
  | PerformanceByVenueTableColumnIDs.ORDERS
  | PerformanceByVenueTableColumnIDs.AVG_BASKET_SIZE_WITH_VAT
  | PerformanceByVenueTableColumnIDs.AVG_BASKET_SIZE_NO_VAT
  | PerformanceByVenueTableColumnIDs.AVG_BASKET_SIZE_ITEMS;

export type PerformanceByVenueTotalItem = PerformanceByVenueTotal & {
  name: string;
  id: string;
  currency: Currency;
};

export type PerformanceByVenueTableProps = {
  className?: string;
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  items: PerformanceByVenueTotalItem[];
  handleSort?: (sort: InfiniteTable.SortState<VenueTableSortableColumnIDs>) => void;
  sort?: InfiniteTable.SortState<VenueTableSortableColumnIDs>;
  visibleRowsCount?: number;
  isVATEnabled: boolean;
};

const TrimmedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const getColumnsDefinition = (
  intl: IntlShape,
  isVATEnabled: boolean,
  handleSort?: (sort: InfiniteTable.SortState<VenueTableSortableColumnIDs>) => void,
) => {
  const columns = [
    {
      id: PerformanceByVenueTableColumnIDs.NAME,
      label: intl.formatMessage({ id: 'insights.performance.table.venue' }),
      render: (record: PerformanceByVenueTotalItem) => (
        <TrimmedText title={record.name ?? undefined}>{record.name}</TrimmedText>
      ),
      width: 30,
    },
    {
      id: PerformanceByVenueTableColumnIDs.SALES,
      label: intl.formatMessage({ id: 'insights.performance.table.total-sales' }),
      render: (record: PerformanceByVenueTotalItem) => (
        <MonetaryAmount amount={getSales(record, isVATEnabled)} currency={record.currency} />
      ),
      enableSort: true,
      handleSort: (sortOrder: InfiniteTable.SortOrder) =>
        handleSort?.({ sortBy: PerformanceByVenueTableColumnIDs.SALES, sortOrder }),
      width: 10,
    },
    {
      id: PerformanceByVenueTableColumnIDs.ORDERS,
      label: intl.formatMessage({ id: 'insights.performance.table.total-orders' }),
      render: (record: PerformanceByVenueTotalItem) => record.orders,
      enableSort: true,
      handleSort: (sortOrder: InfiniteTable.SortOrder) =>
        handleSort?.({ sortBy: PerformanceByVenueTableColumnIDs.ORDERS, sortOrder }),
      width: 10,
    },
    {
      id: PerformanceByVenueTableColumnIDs.AVG_BASKET_SIZE_NO_VAT,
      label: intl.formatMessage({ id: 'insights.performance.table.avg-basket-size' }),
      render: (record: PerformanceByVenueTotalItem) => (
        <MonetaryAmount amount={getBasketSize(record, isVATEnabled)} currency={record.currency} />
      ),
      enableSort: true,
      handleSort: (sortOrder: InfiniteTable.SortOrder) =>
        handleSort?.({ sortBy: PerformanceByVenueTableColumnIDs.AVG_BASKET_SIZE_NO_VAT, sortOrder }),
      width: 10,
    },
    {
      id: PerformanceByVenueTableColumnIDs.AVG_BASKET_SIZE_ITEMS,
      label: intl.formatMessage({ id: 'insights.performance.table.avg-basket-size-items' }),
      render: (record: PerformanceByVenueTotalItem) => record.avgBasketSizeItems,
      enableSort: true,
      handleSort: (sortOrder: InfiniteTable.SortOrder) =>
        handleSort?.({ sortBy: PerformanceByVenueTableColumnIDs.AVG_BASKET_SIZE_ITEMS, sortOrder }),
      width: 10,
    },
  ];
  const columnsWidthSum = columns.reduce((acc, curr) => acc + curr.width, 0);

  return columns.map(column => ({
    ...column,
    width: (column.width / columnsWidthSum) * 100,
  }));
};

const PerformanceByVenueTable = ({
  className,
  items,
  fetchingNextPage,
  fetchingPreviousPage,
  sort,
  onFetchNextPage,
  onFetchPreviousPage,
  handleSort,
  visibleRowsCount,
  isVATEnabled,
}: PerformanceByVenueTableProps) => {
  const intl = useIntl();
  const columns = useMemo(() => getColumnsDefinition(intl, isVATEnabled, handleSort), [intl, handleSort, isVATEnabled]);

  return (
    <InfiniteTable.Root
      className={className}
      items={items}
      fetchingNextPage={fetchingNextPage}
      fetchingPreviousPage={fetchingPreviousPage}
      onFetchNextPage={onFetchNextPage}
      onFetchPreviousPage={onFetchPreviousPage}
      sort={sort}
      visibleRowsCount={visibleRowsCount}
      columns={columns}
    />
  );
};

export default PerformanceByVenueTable;
